import { RegisterComponent } from '@/common/register-component';
import {
  addDaysToCurrentDate,
  convertMinutesToHoursAndMinutes,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  isSameDay,
  isSameMonth,
  normalizeDateInput,
  translateMonth,
} from '@/utils/utils';
import { Component, Element, Event, EventEmitter, Host, Prop, State, Watch, h } from '@stencil/core';
import { timeDay } from 'd3-time';
import { NylasSchedulerConnector } from '../../..';
import { LANGUAGE_CODE } from '@/common/constants';
import { NylasScheduling } from '../nylas-scheduling/nylas-scheduling';
import { ConfigSettings } from '@/stores/scheduler-store';
import i18next from '@/utils/i18n';
import { debug } from '@/utils/utils';
import { ThemeConfig } from '@nylas/core';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Initialize dayjs plugins needed for timezone handling
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * The `nylas-date-picker` component is a UI component that allows users to select a date.
 *
 * @part ndp - The date picker host.
 * @part ndp__title - The title.
 * @part ndp__month-header - The month header.
 * @part ndp__month-button - The month button.
 * @part ndp__day - The day.
 * @part ndp__date - The date.
 * @part ndp__date--selected - The selected date.
 * @part ndp__date--current-day - The current day.
 * @part ndp__date--current-month - The dates in the current month.
 * @part ndp__date--disabled - The disabled dates.
 */
@Component({
  tag: 'nylas-date-picker',
  styleUrl: 'nylas-date-picker.scss',
  shadow: true,
})
export class NylasDatePicker {
  /**
   * The host element.
   * Used to manage the host element of the provider.
   */
  @Element() private host!: HTMLNylasDatePickerElement;

  /**
   * @standalone
   * The dates that are selectable.
   */
  @Prop() selectableDates?: Array<Date | string | number>;

  /**
   * @standalone
   * The config settings.
   */
  @Prop() configSettings?: ConfigSettings;

  /**
   * @standalone
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: any;

  /**
   * The selected timezone.
   */
  @Prop({ attribute: 'selected-timezone' }) readonly selectedTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

  /**
   * The selected date.
   */
  @Prop() selectedDate?: Date | string | number;

  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;

  /**
   * The loading state.
   */
  @Prop() readonly isLoading?: boolean;

  /**
   * The event duration.
   */
  @Prop() readonly eventDuration?: number;

  /**
   * The month to display.
   */
  @State() month: Date = normalizeDateInput(this.selectedDate) || new Date();

  /**
   * The dates to render.
   */
  @State() dates: Date[] = this.getDates();

  /**
   * The state to disable the next month button.
   */
  @State() disableNextMonthButton: boolean = false;

  /**
   * The state to store the selected dates as per the timezone.
   */
  @State() selectableDatesTimezone: Date[] = [];

  /**
   * This event is fired when a date is selected.
   */
  @Event() dateSelected!: EventEmitter<Date>;

  /**
   * This event is fired when the month is changed.
   */
  @Event() monthChanged!: EventEmitter<Date>;

  @Watch('configSettings')
  configSettingsChanged(_newConfigSettings: ConfigSettings) {
    this.updateNextMonthButtonState();
  }

  private updateNextMonthButtonState() {
    const nextMonth = new Date(this.month.getFullYear(), this.month.getMonth() + 1, 1);
    const availableDaysInFuture = this.configSettings?.scheduler?.available_days_in_future;
    if (availableDaysInFuture === undefined) {
      return;
    }
    const endDate = addDaysToCurrentDate(new Date(), availableDaysInFuture);
    this.disableNextMonthButton = endDate.getTime() < nextMonth.getTime();
  }

  @Watch('selectedLanguage')
  selectedLanguageChanged(newLanguage: string) {
    i18next.changeLanguage(newLanguage);
  }

  @Watch('themeConfig')
  themeConfigChanged(newThemeConfig: ThemeConfig) {
    this.applyThemeConfig(newThemeConfig);
  }

  @Watch('selectedDate')
  selectedDateChanged(newSelectedDate: Date | string | number) {
    const normalizedSelectedDate = normalizeDateInput(newSelectedDate);
    if (!normalizedSelectedDate) {
      this.month = new Date();
    } else {
      // Convert selectedDate to a browser-local Date with the correct day in the selected timezone
      const localizedDate = this.convertDatesToTimezone([normalizedSelectedDate], this.selectedTimezone)[0];
      if (!isSameMonth(localizedDate, this.month)) {
        this.month = localizedDate;
      }
    }
    this.dates = this.getDates();
  }

  /**
   * Compare a calendar grid date (browser-local timezone) with a date that may be in a different timezone.
   * Extracts the day from targetDate in the selected timezone and compares with the calendar date's
   * browser-local day components. This prevents the "one day behind" bug (CUST-5246) when the selected
   * timezone is ahead of the browser timezone (e.g., Australia/Sydney vs America/New_York).
   */
  private isSameDayAsSelected(calendarDate: Date, targetDate: Date | string | number): boolean {
    const normalizedTargetDate = normalizeDateInput(targetDate);
    if (!normalizedTargetDate) {
      return false;
    }
    const targetInTz = dayjs(normalizedTargetDate).tz(this.selectedTimezone);
    return calendarDate.getFullYear() === targetInTz.year() && calendarDate.getMonth() === targetInTz.month() && calendarDate.getDate() === targetInTz.date();
  }

  /**
   * Helper method to convert dates to the specified timezone, preserving only the date part
   */
  private convertDatesToTimezone(dates: Array<Date | string | number> | undefined, targetTimezone: string): Date[] {
    if (!dates || dates.length === 0) {
      return [];
    }

    return dates
      .map(d => normalizeDateInput(d))
      .filter((d): d is Date => !!d)
      .map(d => {
        const tzDate = dayjs(d).tz(targetTimezone);
        return new Date(tzDate.year(), tzDate.month(), tzDate.date());
      });
  }

  @Watch('selectedTimezone')
  selectedTimezoneChanged(newSelectedTimezone: string) {
    this.selectableDatesTimezone = this.convertDatesToTimezone(this.selectableDates, newSelectedTimezone);

    // Recompute month when selectedDate exists — the same absolute instant may
    // land in a different calendar month in the new timezone.
    const normalizedSelectedDate = normalizeDateInput(this.selectedDate);
    if (normalizedSelectedDate) {
      const localizedDate = this.convertDatesToTimezone([normalizedSelectedDate], newSelectedTimezone)[0];
      if (!isSameMonth(localizedDate, this.month)) {
        this.month = localizedDate;
        this.dates = this.getDates();
        this.updateNextMonthButtonState();
      }
    }
  }

  @Watch('selectableDates')
  selectableDatesChanged(newSelectableDates: Array<Date | string | number> | undefined) {
    this.selectableDatesTimezone = this.convertDatesToTimezone(newSelectableDates, this.selectedTimezone);
  }

  connectedCallback() {
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  disconnectedCallback() {}

  componentWillLoad() {}

  componentDidLoad() {
    debug(`[nylas-date-picker] Component did load`);
    const normalizedSelectedDate = normalizeDateInput(this.selectedDate);
    this.month = normalizedSelectedDate ? this.convertDatesToTimezone([normalizedSelectedDate], this.selectedTimezone)[0] : new Date();
    this.dates = this.getDates();
    this.selectableDatesTimezone = this.convertDatesToTimezone(this.selectableDates, this.selectedTimezone);
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  /**
   * Get the dates to render, including the days from the previous and next months.
   * @returns The dates to render.
   */
  private getDates() {
    const lastDayOfMonth = getLastDayOfMonth(this.month);
    const firstDayOfMonth = getFirstDayOfMonth(this.month);

    return timeDay.range(timeDay.offset(firstDayOfMonth, -firstDayOfMonth.getDay()), timeDay.offset(lastDayOfMonth, 7 - lastDayOfMonth.getDay()));
  }

  /**
   * Select a date.
   * @param date The date to select (in local timezone from the calendar grid).
   *
   * The calendar grid displays dates as local timezone Date objects. When a user clicks
   * on a date, we need to convert it to represent that same calendar day in the
   * SELECTED timezone, not the user's local timezone. This ensures that if the user
   * has selected America/Los_Angeles but their local timezone is America/Toronto,
   * clicking on "January 8th" will create a Date representing January 8th in Los Angeles.
   */
  private selectDate(date?: Date) {
    if (!date) {
      this.dateSelected.emit(date);
      return;
    }

    // Get the calendar day (year, month, date) from the clicked date (which is in local timezone)
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    // Create a new Date representing noon on that calendar day in the SELECTED timezone
    // We use noon to avoid edge cases near midnight
    const dateInSelectedTimezone = dayjs.tz(`${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')} 12:00`, this.selectedTimezone);

    this.dateSelected.emit(dateInSelectedTimezone.toDate());
  }

  /**
   * Change the month.
   * @param change The change in months.
   */
  private changeMonth(change: number) {
    this.month = new Date(this.month.getFullYear(), this.month.getMonth() + change, 1);
    const nextMonth = new Date(this.month.getFullYear(), this.month.getMonth() + 1, 1);
    const availableDaysInFuture = this.configSettings?.scheduler?.available_days_in_future;
    const endDate = addDaysToCurrentDate(new Date(), availableDaysInFuture);
    if (endDate.getTime() < nextMonth.getTime()) {
      this.disableNextMonthButton = true;
    } else {
      this.disableNextMonthButton = false;
    }
    this.dates = this.getDates();
    this.monthChanged.emit(this.month);
  }

  private isSelected(date: Date | undefined, selectedDate: Date | string | number | undefined, selectableDates: Array<Date | string | number> | undefined) {
    return date && selectedDate && this.isSameDayAsSelected(date, selectedDate) && selectableDates && selectableDates?.length > 0;
  }

  private isDisabled(date: Date | undefined) {
    if (!date || !this.selectableDatesTimezone || this.selectableDatesTimezone.length === 0) {
      return true;
    }

    // Compare only the date parts (year, month, day), ignoring time
    return !this.selectableDatesTimezone.some(d => {
      return date.getFullYear() === d.getFullYear() && date.getMonth() === d.getMonth() && date.getDate() === d.getDate();
    });
  }

  @RegisterComponent<NylasDatePicker, NylasSchedulerConnector, Exclude<NylasScheduling['stores'], undefined>>({
    name: 'nylas-date-picker',
    stateToProps: new Map([
      ['scheduler.selectableDates', 'selectableDates'],
      ['scheduler.selectedDate', 'selectedDate'],
      ['scheduler.isLoading', 'isLoading'],
      ['scheduler.configSettings', 'configSettings'],
      ['scheduler.eventDuration', 'eventDuration'],
      ['scheduler.selectedLanguage', 'selectedLanguage'],
      ['scheduler.themeConfig', 'themeConfig'],
      ['scheduler.selectedTimezone', 'selectedTimezone'],
    ]),
    eventToProps: {
      dateSelected: async (event: CustomEvent<Date>, nylasSchedulerConnector: NylasSchedulerConnector) => {
        debug('nylas-date-picker', 'dateSelected', event.detail);
        nylasSchedulerConnector.scheduler.selectDate(event.detail);
      },
      monthChanged: async (event: CustomEvent<Date>, _nylasSchedulerConnector: NylasSchedulerConnector) => {
        debug('nylas-date-picker', 'monthChanged', event.detail);
      },
    },
    fireRegisterEvent: true,
  })
  render() {
    return (
      <Host part="ndp">
        <div class="nylas-date-picker">
          <div class="title" part="ndp__title">
            {this.configSettings?.name ? (
              <h1>{this.configSettings?.name}</h1>
            ) : (
              this.configSettings?.organizer?.name && (
                <h1>
                  <person-icon />
                  {this.configSettings?.organizer?.name || 'Organizer'}
                </h1>
              )
            )}
            <p>
              <clock-icon />
              {this.eventDuration ? convertMinutesToHoursAndMinutes(this.eventDuration) : `- ${i18next.t('time.minutes')}`}
            </p>
          </div>
          <div class={'header flex-row'}>
            <h2 part={'ndp__month-header'}>
              <strong>{translateMonth(this.month.toLocaleDateString('en-US', { month: 'long' }).toLowerCase())}</strong>
              &nbsp;
              {this.month.toLocaleDateString(undefined, { year: 'numeric' })}
            </h2>
            <div class={'pagination'}>
              <button
                title="Previous month"
                onClick={() => this.changeMonth(-1)}
                class={{ 'chevron-left': true, 'button': true }}
                disabled={this.month <= new Date()}
                part="ndp__month-button"
              >
                <chevron-icon />
              </button>
              <button
                title="Next month"
                onClick={() => this.changeMonth(1)}
                class={{ 'chevron-right': true, 'button': true }}
                disabled={this.disableNextMonthButton}
                part="ndp__month-button"
              >
                <chevron-icon />
              </button>
            </div>
          </div>

          <div class={'dates'}>
            {[
              i18next.t('days.sunday'),
              i18next.t('days.monday'),
              i18next.t('days.tuesday'),
              i18next.t('days.wednesday'),
              i18next.t('days.thursday'),
              i18next.t('days.friday'),
              i18next.t('days.saturday'),
            ].map(day => {
              return (
                <div class={'day'} part="ndp__day">
                  {day}
                </div>
              );
            })}
            {this.dates.map((date, i) => {
              const isDisabled = this.isDisabled(date);

              if (this.isLoading) {
                return (
                  <button
                    disabled
                    class={{
                      'date day-skeleton': true,
                      'current-month': isSameMonth(date, this.month),
                    }}
                    style={{ animationDelay: `${i * 20}ms` }}
                    part={`ndp__date ndp__date--disabled`}
                  >
                    {date.getDate()}
                  </button>
                );
              }
              return (
                <button
                  class={{
                    'date': true,
                    'selected': !!(this.selectedDate && this.isSameDayAsSelected(date, this.selectedDate) && this.selectableDates && this.selectableDates?.length > 0),
                    'current-day': isSameDay(date, new Date()),
                    'current-month': isSameMonth(date, this.month),
                  }}
                  aria-label={date.toLocaleDateString(undefined, { dateStyle: 'full' })}
                  disabled={isDisabled}
                  onClick={() => this.selectDate(date)}
                  part={`ndp__date ${this.isSelected(date, this.selectedDate, this.selectableDates) ? 'ndp__date--selected' : ''} ${isSameDay(date, new Date()) ? 'ndp__date--current-day' : ''} ${isSameMonth(date, this.month) ? 'ndp__date--current-month' : ''}`}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      </Host>
    );
  }
}
